import { useState, useEffect } from "react";
import { Plus, Trash2, Save, X, Check, Loader2 } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import RichTextEditor from "../ui/RichTextEditor";
import { updateFieldContent } from "../../services/consultationform";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import { FormField } from "../../types/forms";

interface PolicySectionProps {
  userId: string;
  templateId: string;
  isEditable?: boolean;
  fields?: FormField[];
}

interface PolicyField {
  id: string;
  label: string;
  checked: boolean;
}

export default function PolicySection({
  userId,
  templateId,
  isEditable = false,
  fields,
}: PolicySectionProps) {
  const storageKey = `${userId}_${templateId}_policyfields`;

  const [storageFields, setFields] = useLocalStorage<PolicyField[]>(storageKey, [
    {
      id: crypto.randomUUID(),
      label: "I have read and understood the Privacy Policy.",
      checked: false,
    },
    {
      id: crypto.randomUUID(),
      label: "I consent to the processing and storage of my data as described.",
      checked: false,
    },
    {
      id: crypto.randomUUID(),
      label: "I understand that my personal data is used only for service purposes.",
      checked: false,
    },
    {
      id: crypto.randomUUID(),
      label: "I can request deletion of my data at any time.",
      checked: false,
    },
  ]);

  const [availableFields] = useState<string[]>([
    "I have read and understood the Privacy Policy.",
    "I consent to the processing and storage of my data.",
    "I understand that my personal data is used only for service purposes.",
    "I can request deletion of my data at any time.",
  ]);

  const [customInput, setCustomInput] = useState("");
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [tempContent, setTempContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleToggleCheck = (id: string) => {
    if (!isEditable) {
      setFields((prev) =>
        prev.map((f) => (f.id === id ? { ...f, checked: !f.checked } : f))
      );
    }
  };

  const handleAddField = (label: string) => {
    if (!isEditable) return;
    const trimmed = label.trim();
    if (!trimmed || storageFields.some((f) => f.label === trimmed)) return;
    setFields((prev) => [
      ...prev,
      { id: crypto.randomUUID(), label: trimmed, checked: false },
    ]);
    setCustomInput("");
  };

  const handleDeleteField = (id: string) => {
    if (!isEditable) return;
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  // Sync with props but allow local updates
  const [localFields, setLocalFields] = useState<any[]>([]);

  useEffect(() => {
    if (fields && fields.length > 0) {
      setLocalFields(fields.map(f => ({
        id: String(f.id),
        label: f.options?.content || f.label,
        checked: false
      })));
    } else {
      setLocalFields(storageFields);
    }
  }, [fields, storageFields]);

  const displayFields = localFields;

  const handleStartEdit = (field: any) => {
    if (!fields) return;
    setEditingFieldId(field.id);
    setTempContent(field.label); // Initialize editor with current label (HTML)
  };

  const handleSaveEdit = async (fieldId: string) => {
    setIsSaving(true);
    try {
      // Optimistic update
      setLocalFields(prev => prev.map(f =>
        f.id === fieldId ? { ...f, label: tempContent } : f
      ));

      await updateFieldContent(fieldId, tempContent);
      setEditingFieldId(null);
    } catch (err) {
      console.error("Failed to save field content:", err);
      // Revert if needed (optional, for now we assume success or user retries)
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
        Privacy Policy
      </h2>
      <div className="space-y-3">
        {displayFields.length > 0 ? (
          displayFields.map((field) => (
            <div key={field.id} className="flex items-start gap-2">

              <div className="flex-1 text-sm text-gray-700 leading-snug">
                {editingFieldId === field.id ? (
                  <div className="space-y-2">
                    <RichTextEditor
                      value={tempContent}
                      onChange={setTempContent}
                      rows={4}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingFieldId(null)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                      <button
                        disabled={isSaving}
                        onClick={() => handleSaveEdit(field.id)}
                        className="p-1 text-green-500 hover:text-green-600 disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => isEditable && fields && handleStartEdit(field)}
                    className={`${isEditable && fields ? "cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition-colors" : ""}`}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: field.label }}
                    />
                  </div>
                )}
              </div>
              {isEditable && !fields && (
                <button
                  onClick={() => handleDeleteField(field.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors mt-1"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-400 italic text-center py-2">
            No policies added yet.
          </p>
        )}
      </div>

      {isEditable && !fields && (
        <div className="border-t border-gray-200 pt-3 space-y-2">
          <label className="block text-xs font-medium text-gray-600">
            Add New Policy
          </label>
          <div className="flex items-center gap-2">
            <Select onValueChange={(value) => handleAddField(value)}>
              <SelectTrigger className="flex-1 h-9 text-sm border-gray-300 bg-gray-50">
                <SelectValue placeholder="Choose a policy to add..." />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-60 overflow-y-auto">
                {availableFields.map((label) => (
                  <SelectItem key={label} value={label}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="text"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              placeholder="Or enter custom policy..."
              className="flex-1 h-9 px-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              onClick={() => handleAddField(customInput)}
              className="p-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-all"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
